// Checking the player's pencil marks as they go (Settings: "Check pencil marks"), at the
// mistake-checking level. Fogged cells are checked like any other (Marko, 2026-11-01).
import type { MistakeChecking } from '@zinc/shared';
import { values, type GridState } from './game';
import type { Geometry } from './geometry';

/** Per cell, the marks to show in red. */
export interface WrongMarks {
  corner: Map<number, Set<string>>;
  centre: Map<number, Set<string>>;
  position: Map<number, Set<string>>;
}

export const NO_WRONG_MARKS: WrongMarks = {
  corner: new Map(),
  centre: new Map(),
  position: new Map(),
};

function flag(into: Map<number, Set<string>>, cell: number, digit: string) {
  let set = into.get(cell);
  if (!set) into.set(cell, (set = new Set()));
  set.add(digit);
}

/**
 * The marks that are wrong.
 *
 * - Conflicts (and Solution): a mark whose digit is already placed (given or entered) in the
 *   same row, column or region.
 * - Solution, also: a cell's centre or position marks (Simple mode: its pencil marks) that no
 *   longer include
 *   its correct digit, all of them, since what's wrong is the digit that's missing; and
 *   SudokuPad corner marks, read as "this digit is in one of these cells of the region": when the
 *   region's corner marks for a digit leave out the cell where it belongs, all of them.
 *
 * Without a stored solution, Solution falls back to Conflicts.
 */
export function wrongMarks(
  g: Geometry,
  s: GridState,
  level: MistakeChecking,
  markStyle: 'sudokupad' | 'simple',
): WrongMarks {
  if (level === 'off') return NO_WRONG_MARKS;
  const out: WrongMarks = { corner: new Map(), centre: new Map(), position: new Map() };
  const vals = values(g, s);

  // Rule conflicts with placed digits.
  s.cells.forEach((cell, i) => {
    if (vals[i] !== null) return;
    for (const key of ['corner', 'centre', 'position'] as const) {
      for (const d of cell[key]) {
        if (g.peers[i]!.some((p) => vals[p] === d)) flag(out[key], i, d);
      }
    }
  });
  if (level !== 'solution' || !g.solution) return out;
  const solution = g.solution;

  // Candidates that lost the right digit: centre marks, and Simple mode's pencil marks.
  const candidateKeys =
    markStyle === 'simple'
      ? (['corner', 'centre', 'position'] as const)
      : (['centre', 'position'] as const);
  s.cells.forEach((cell, i) => {
    if (vals[i] !== null) return;
    for (const key of candidateKeys) {
      const marks = cell[key];
      if (marks.length > 0 && !marks.includes(solution[i]!)) {
        for (const d of marks) flag(out[key], i, d);
      }
    }
  });

  // SudokuPad corner marks: the region's marks for a digit must include the digit's cell.
  if (markStyle === 'sudokupad') {
    const regions = new Map<number, number[]>();
    g.cells.forEach((_, i) => {
      const r = g.region[i]!;
      if (r < 0) return;
      const list = regions.get(r);
      if (list) list.push(i);
      else regions.set(r, [i]);
    });
    for (const cells of regions.values()) {
      const marked = new Map<string, number[]>();
      for (const i of cells) {
        if (vals[i] !== null) continue;
        for (const d of s.cells[i]!.corner) {
          const list = marked.get(d);
          if (list) list.push(i);
          else marked.set(d, [i]);
        }
      }
      for (const [d, holding] of marked) {
        const home = cells.find((i) => solution[i] === d);
        if (home === undefined || holding.includes(home)) continue;
        for (const i of holding) flag(out.corner, i, d);
      }
    }
  }
  return out;
}
